/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDispatch } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { useAuth } from '../modules/auth'
import { useWebSocketHook } from './index'
import { createColumn } from '../services/column'
import { webSocketOperations } from '../constants'
import { capitalizeEachWord } from '../helper-functions/CapitalizeName'
import { setColumnUpdatedAt } from '../store/slice/last-update-slice'

// Default attributes for a column created from a checklist task. The orange
// is b4igo's corporate colour; "Before I go" is the auto-name the spec asks
// for (and the marker we use to pick the default column on the next open).
export const CHECKLIST_COLUMN_DEFAULT_NAME = 'Before I go'
export const CHECKLIST_COLUMN_DEFAULT_COLOR = '#FF7C00'

interface Params {
  boardId: string
}

// Creates a board column on demand from the "Create task from checklist"
// modal and broadcasts the same columnCreate event the kanban "Add column"
// flow uses (ColumnButton.tsx). Boards listen for that event via
// useGetColumns and refetch, so the new column surfaces as the last column.
export function useCreateChecklistColumn({ boardId }: Params) {
  const dispatch: Dispatch<any> = useDispatch()
  const { currentUser } = useAuth()
  const { id: userId, fullName } = currentUser?.data?.user || {}
  const { sendJsonMessage }: any = useWebSocketHook()

  return async ({
    name,
    color,
    columns,
  }: {
    name: string
    color: string
    columns: any[]
  }) => {
    if (!boardId) throw new Error('Missing board context')

    // Append after the current last column. Mirror ColumnButton's position
    // logic (max existing position + 1) so it lands before the Add Column
    // button on the kanban.
    const maxPosition = (columns || []).reduce(
      (max: number, c: any) => Math.max(max, Number(c?.position) || 0),
      0
    )
    const position = (columns?.length || 0) === 0 ? 0 : maxPosition + 1

    const data = {
      created_by: fullName,
      last_modified_by: fullName,
      color,
      position,
      name,
      boardId,
      version: null,
      status: null,
    }

    const res = await createColumn(data, userId)
    const newColumn = res.data.data

    sendJsonMessage({
      event: 'publish',
      channel: boardId,
      userId,
      command: 'CREATE',
      operation: webSocketOperations.columnCreate,
      payload: {
        newColumn,
        boardId,
        alert: `${capitalizeEachWord(fullName)} created a new column ${newColumn.name}.`,
      },
    })

    // Signal the board page to refetch its columns. The websocket above only
    // reaches OTHER clients (the backend doesn't echo to the sender), so the
    // sender's own kanban — sitting behind the open card modal — wouldn't
    // otherwise learn about the new column until a manual reload. Board.tsx
    // handles this signal with a *silent* refetch (no skeleton): the one-shot
    // gate keeps the changed columnResponse from triggering a card refetch, and
    // `silent` keeps the column skeleton from swapping out BoardDnD — either of
    // which would unmount the open card modal mid-create and lose the unsaved
    // checklist→task link before it's persisted.
    dispatch(setColumnUpdatedAt(Date.now()))

    return newColumn
  }
}
